import { FeaCustomScaleColorConfiguration } from "src/app/fea/rendering/fea-color-configuration";

type StringNumberDictionary = {
  [key: string]: number;
};

export class Scale {
  Max: number
  Min: number
  High: number
  Low: number
  IsUnitary: boolean
  Colors: Map<string, number>
  UseLog: boolean
  Slope: number
  Intercept: number
  SubsetInfo: StringNumberDictionary

  constructor(max: number, min: number, isUnitary: boolean, useLog: boolean = false, colors: Map<string, number> = null) {
    this.SubsetInfo = {}
    this.IsUnitary = isUnitary
    this.UseLog = useLog
    this.setMainPoints(max, min)
    this.Colors = colors == null ? this.getDefaultColors() : colors;

    if (this.IsUnitary) {
      [this.Slope, this.Intercept] = this.solveLinearEquation();
    }
  }

  public getScaleAdjustedColorConfiguration(): FeaCustomScaleColorConfiguration{
    return new FeaCustomScaleColorConfiguration(this.getScaleAdjustedColors(this.Colors))
  }

  private getScaleAdjustedColors(colors: Map<string, number>): Map<string, number> {
    const newColors = new Map<string, number>();
    for (let [color, value] of colors.entries()) {
      newColors.set(color, this.getMarkPoint(value));
    }
    return newColors;
  }

  private setMainPoints(max: number, min: number): void {
    if (this.IsUnitary && this.UseLog) {
      this.Max = 1
      this.Min = 1e-8

      this.High = 1e-4
      this.Low = 1e-7
    }
    else if (this.IsUnitary) {
      this.Max = 1
      this.Min = 0

      this.High = 0.9
      this.Low = this.Min
    }
    else {
      this.Max = Math.round( ( max + Number.EPSILON ) * 100 ) / 100
      this.Min = Math.round( ( min + Number.EPSILON ) * 100 ) / 100

      this.High = this.Max
      this.Low = this.Min
    }
  }

  private getDefaultColors(): Map<string, number> {
    let colors = new Map()
    if (this.IsUnitary && this.UseLog) {
      colors.set('limegreen', 1e-7)
      colors.set('yellow', 1e-6)
      colors.set('orange', 1e-5)
      colors.set('red', 1e-4)
    }
    else if (this.IsUnitary) {
      colors.set('limegreen', 0)
      colors.set('yellow', 0.75)
      colors.set('red', 0.9)
    }
    else {
      colors.set('limegreen', 0)
      colors.set('yellow', 0.75)
      colors.set('red', 1)
    }
    return colors;
  }

  private solveLinearEquation(): [number, number] {
    let [x1, y1] = [Math.log10(this.Min), 0];
    let [x2, y2] = [Math.log10(this.Max), 1];
    let slope = (y2 - y1) / (x2 - x1);
    let intercept = y1 - slope * x1;
    return [slope, intercept];
  }

  private getMarkPoint(point: number) {
    if (this.IsUnitary && point >= this.Min && point <= this.Max) return point;
    return (this.High - this.Low) * point + this.Low
    //return Math.round( ( mark + Number.EPSILON ) * 100 ) / 100
  }

  public getColorScaleStops(): { stop: number, color: string }[] { // Use to color the scale
    var colorStops: { stop: number, color: string }[] = []

    var colorConfig = this.getScaleAdjustedColorConfiguration();

    colorStops.push(this.getColorStop(colorConfig, this.Min))
    colorStops.push(this.getColorStop(colorConfig, this.Max))

    if (this.isBetweenMinMax(this.Low)) colorStops.push(this.getColorStop(colorConfig, this.Low))
    for (let value of [...this.Colors.values()].slice(1, -1)) {
      let midPoint = this.getMarkPoint(value);
      if (this.isBetweenMinMax(midPoint)) colorStops.push(this.getColorStop(colorConfig, midPoint))
    }
    if (this.isBetweenMinMax(this.High)) colorStops.push(this.getColorStop(colorConfig, this.High))

    return colorStops
  }

  private isBetweenMinMax(point: number) {
    return this.Min < point && point < this.Max;
  }

  private getColorStop(colorConfig: FeaCustomScaleColorConfiguration, point: number): { stop: number, color: string } {
    return {
      stop: this.getAdjustedMarkPoint(point),
      color: colorConfig.getColor(point)
    }
  }

  private getAdjustedMarkPoint(point: number) { // get point adjusted between 0 and 1
    if (this.UseLog) return this.logToValue(point);
    return (point - this.Min) / (this.Max - this.Min)
    //return Math.round( ( adjusted + Number.EPSILON ) * 100 ) / 100
  }

  private logToValue(val: number): number {
    let logVal = Math.log10(val);
    return this.Slope * logVal + this.Intercept;
  }

  public setSubsetInfo(data: [string, number][]): void {
    this.resetSubsetInfo();
    for (const [key, value] of data) {
        this.SubsetInfo[key] = value;
    }
  }

  public resetSubsetInfo(): void {
    this.SubsetInfo = {}
  }

  public isSubsetInfoEmpty(): boolean {
    return Object.keys(this.SubsetInfo).length === 0;
  }
}
